import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useState } from "react";
import classnames from "classnames";

import { useApolloClient } from "util/graphql";
import { useActiveOrganization } from "common/account/active_organization";
import AlertMessage from "common/core/alert_message";
import Button from "common/core/button";

import { type PlaidInfo, verifyAch } from "../ach_util";
import Styles from "../ach.module.scss";
import type { Payment_node_Organization_defaultPaymentSource as PaymentSource } from "../graphql/payment_query.graphql";
import Tooltip from "../../../core/tooltip";
import Icon from "../../../core/icon";

const MESSAGES = defineMessages({
  change: {
    id: "1ab24df3-d2b3-4cac-bee6-ab9925a4abf3",
    defaultMessage: "Change",
  },
  connectAccount: {
    id: "8287a086-a613-4b32-a882-77bcb7fefcac",
    defaultMessage: "Connect Account",
  },
});

function ACHButton({
  buttonLabel,
  onChange,
}: {
  buttonLabel: string;
  onChange: (info: PlaidInfo) => void;
}) {
  const client = useApolloClient();
  const [activeOrganizationId] = useActiveOrganization();
  const [verifying, setVerifying] = useState(false);
  const [plaidError, setPlaidError] = useState(false);

  const connectBankAccount = async () => {
    setPlaidError(false);
    setVerifying(true);
    try {
      await verifyAch(client, activeOrganizationId!, onChange);
    } catch {
      setPlaidError(true);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <Button
        buttonColor="action"
        variant="secondary"
        onClick={connectBankAccount}
        isLoading={verifying}
      >
        {buttonLabel}
      </Button>
      {plaidError && (
        <div>
          <AlertMessage kind="danger">
            <FormattedMessage
              id="92e7b9ef-4e42-438a-9814-f95e6155aa9d"
              defaultMessage="Sorry, something went wrong. Please try again."
            />
          </AlertMessage>
        </div>
      )}
    </>
  );
}

function ACHField({ existingAccount }: { existingAccount: PaymentSource | null }) {
  const intl = useIntl();
  const [newAccount, setNewAccount] = useState<PlaidInfo>();

  const onAchChange = (plaidInfo: PlaidInfo) => {
    setNewAccount(plaidInfo);
  };

  let accountName = null;
  if (newAccount) {
    accountName = <span>{newAccount.accountName}</span>;
  } else if (existingAccount) {
    accountName = (
      <span>
        {existingAccount.name} •••• {existingAccount.last4}
      </span>
    );
  }

  return (
    <div className={Styles.accountLabelGroup}>
      <span className={Styles.label}>{accountName}</span>
      <ACHButton
        onChange={onAchChange}
        buttonLabel={
          existingAccount
            ? intl.formatMessage(MESSAGES.change)
            : intl.formatMessage(MESSAGES.connectAccount)
        }
      />
    </div>
  );
}

export function AchFormV2({ paymentSource }: { paymentSource: PaymentSource }) {
  const existingAccount = paymentSource.type === "AchAccount" ? paymentSource : null;

  return (
    <div className={classnames("Form-row", Styles.achItemsGroup)}>
      <label>Account</label>
      <ACHField existingAccount={existingAccount} />

      <Tooltip
        target={<Icon name="question-circle" />}
        placement="top"
        containerClassName={Styles.tooltip}
      >
        <FormattedMessage
          id="c70722a0-4175-42b2-95cc-7d0bd85fdb0f"
          defaultMessage="Allow your company to pay directly with a bank account. These charges will be batched and processed daily."
        />
      </Tooltip>
    </div>
  );
}
